/**
 * PatientSearchCard — box "Cerca Pazienti" nella Dashboard.
 *
 * Filtri:
 *  - Assicurazione (single select)
 *  - Mesi (multi-select)
 *  - Stage (multi-select, raggruppato per le 4 pipeline ammesse)
 *  - Sigle prestazioni con modalità "Anche con" (default) o "Solo con"
 *
 * Endpoint: GET /api/dashboard/patient-search
 *
 * I risultati appaiono sotto il form: tabella con paziente + opportunità
 * matchate (insurance, pipeline, stage corrente, sigle, € totale).
 *
 * Modifiche 25/06/2026:
 *  - Dropdown stage limitato alle 4 pipeline (Assicurazioni RC/Orto +
 *    Indiretti RC/Orto) e raggruppato visivamente per pipeline.
 *  - MultiSelect: chiusura automatica al click fuori dal box (outside click).
 *  - Header risultati con etichetta "Tot Pazienti trovati: XXX" prominente.
 *  - Tabella sigle: mostra tutte le sigle del piano (no slice), evidenzia
 *    in indaco quelle selezionate dal filtro quando mode="also".
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Search, ChevronDown, X as XIcon, Download, Users } from "lucide-react";
import { cachedGet } from "@/lib/apiCache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_IT_LONG = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// Pipeline ammesse nella selezione stage (in ordine di rendering del
// dropdown). I nomi devono corrispondere ESATTAMENTE a quelli configurati
// su GHL (con il trattino " - ", come restituito da
// `/api/dashboard/pipeline-stages`).
const ALLOWED_PIPELINES = [
  "Assicurazioni - Reparto Clinico",
  "Assicurazioni - Ortodonzia",
  "Indiretti - Reparto Clinico",
  "Indiretti - Ortodonzia",
];

function buildMonthOptions() {
  const now = new Date();
  const opts = [];
  let y = now.getFullYear();
  let m = now.getMonth();
  for (let i = 0; i < 36; i += 1) {
    opts.push({ ym: `${y}-${String(m + 1).padStart(2, "0")}`, label: `${MONTH_IT_LONG[m]} ${y}` });
    if (m === 0) { y -= 1; m = 11; } else { m -= 1; }
  }
  return opts;
}

/**
 * Dropdown multi-select con:
 *  - chip-summary nel pulsante input
 *  - chiusura automatica al click fuori dal container (outside-click)
 *  - supporto `options` (flat) OPPURE `groups` ([{name, stages: [{value,label}]}])
 *    per rendering raggruppato (header pipeline + voci stage indentate)
 */
function MultiSelect({ label, options, groups, values, onChange, placeholder, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Outside click handler: chiude il pannello quando si clicca fuori dal box.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  };

  const renderItem = (o) => {
    const val = typeof o === "string" ? o : o.value;
    const lbl = typeof o === "string" ? o : o.label;
    const checked = values.includes(val);
    return (
      <button
        key={val}
        type="button"
        onClick={() => toggle(val)}
        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${checked ? "bg-indigo-50 text-indigo-700" : "text-slate-700"}`}
      >
        <span className={`w-4 h-4 border rounded-sm flex items-center justify-center ${checked ? "bg-indigo-500 border-indigo-500" : "border-slate-300"}`}>
          {checked && <span className="text-white text-[10px] leading-none">✓</span>}
        </span>
        <span className="truncate">{lbl}</span>
      </button>
    );
  };

  const hasGroups = Array.isArray(groups) && groups.length > 0;
  const isEmpty = hasGroups
    ? groups.every((g) => !(g.stages || []).length)
    : (options || []).length === 0;

  return (
    <div className="relative" data-testid={testid} ref={containerRef}>
      {label && <label className="block text-[11px] font-semibold text-slate-500 mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`h-10 w-full text-sm border rounded-lg px-3 bg-white text-left flex items-center justify-between gap-2 ${disabled ? "border-slate-100 text-slate-400" : "border-slate-200 hover:border-slate-300"}`}
      >
        <span className="truncate">
          {values.length === 0 ? (
            <span className="text-slate-400">{placeholder || "Seleziona…"}</span>
          ) : (
            <span className="text-slate-700">
              {values.length} {values.length === 1 ? "selezionato" : "selezionati"}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {isEmpty && (
            <div className="px-3 py-2 text-xs text-slate-400">Nessuna opzione</div>
          )}
          {hasGroups
            ? groups.map((grp) => (
                (grp.stages || []).length > 0 && (
                  <div key={grp.name}>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                      {grp.name}
                    </div>
                    {grp.stages.map((s) => renderItem(s))}
                  </div>
                )
              ))
            : (options || []).map((o) => renderItem(o))}
          {values.length > 0 && (
            <div className="sticky bottom-0 border-t border-slate-100 px-3 py-2 flex justify-between items-center bg-white">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[11px] text-rose-600 hover:underline"
              >
                Deseleziona tutti
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[11px] text-indigo-700 hover:underline"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PatientSearchCard() {
  // ── Form state ──────────────────────────────────────────────────
  const [insurance, setInsurance] = useState("");
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  // mode: "also" (default) | "only"
  const [codesMode, setCodesMode] = useState("also");

  // ── Options ─────────────────────────────────────────────────────
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [insurances, setInsurances] = useState([]);
  const [stagesByPipeline, setStagesByPipeline] = useState([]);
  const [treatmentCodes, setTreatmentCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);

  // ── Results ─────────────────────────────────────────────────────
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  // Carica le opzioni statiche al mount (assicurazioni + stage)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [insRes, stRes] = await Promise.all([
          cachedGet(`${API}/dashboard/insurances-list`),
          cachedGet(`${API}/dashboard/pipeline-stages`),
        ]);
        if (cancelled) return;
        setInsurances(insRes.data?.insurances || []);
        setStagesByPipeline(stRes.data?.by_pipeline || []);
      } catch (e) {
        console.debug("PatientSearchCard: options load failed", e?.response?.status);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Quando cambia l'assicurazione, carica le sigle prestazioni di quel
  // listino (svuotando le sigle scelte). Se assicurazione vuota, lista vuota.
  useEffect(() => {
    let cancelled = false;
    if (!insurance) {
      setTreatmentCodes([]);
      setSelectedCodes([]);
      return () => { cancelled = true; };
    }
    setLoadingCodes(true);
    setSelectedCodes([]);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/treatments`, {
          params: { insurance },
          withCredentials: true,
        });
        if (cancelled) return;
        const treatments = data?.treatments || [];
        setTreatmentCodes(
          treatments
            .map((t) => ({ code: (t.code || "").trim().toUpperCase(), name: t.name || "" }))
            .filter((t) => t.code)
            .sort((a, b) => a.code.localeCompare(b.code)),
        );
      } catch (e) {
        if (!cancelled) setTreatmentCodes([]);
      } finally {
        if (!cancelled) setLoadingCodes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [insurance]);

  // Stage groups: solo le 4 pipeline ammesse, in ordine fisso, con i
  // rispettivi stage (dedup interno mantenendo l'ordine GHL).
  const stageGroups = useMemo(() => {
    const out = [];
    for (const target of ALLOWED_PIPELINES) {
      const p = (stagesByPipeline || []).find((x) => (x.name || "") === target);
      if (!p) continue;
      const seen = new Set();
      const stages = [];
      for (const s of (p.stages || [])) {
        const v = (s || "").trim();
        if (!v) continue;
        const k = v.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        stages.push({ value: v, label: v });
      }
      if (stages.length) out.push({ name: target, stages });
    }
    return out;
  }, [stagesByPipeline]);

  const codeOptions = useMemo(
    () => treatmentCodes.map((t) => ({
      value: t.code,
      label: t.name ? `${t.code} — ${t.name}` : t.code,
    })),
    [treatmentCodes],
  );

  const runSearch = useCallback(async () => {
    setSearching(true);
    setError("");
    try {
      const params = {};
      if (insurance) params.insurance = insurance;
      if (selectedMonths.length) params.months = selectedMonths.join(",");
      if (selectedStages.length) params.stages = selectedStages.join(",");
      if (selectedCodes.length) {
        params.treatment_codes = selectedCodes.join(",");
        params.mode = codesMode;
      }
      const { data } = await axios.get(`${API}/dashboard/patient-search`, {
        params, withCredentials: true,
      });
      setResults(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossibile eseguire la ricerca");
      setResults(null);
    } finally {
      setSearching(false);
    }
  }, [insurance, selectedMonths, selectedStages, selectedCodes, codesMode]);

  const resetAll = () => {
    setInsurance("");
    setSelectedMonths([]);
    setSelectedStages([]);
    setSelectedCodes([]);
    setCodesMode("also");
    setResults(null);
    setError("");
  };

  const exportCsv = useCallback(() => {
    if (!results || !results.patients?.length) return;
    const rows = [];
    rows.push(["Paziente", "Contact ID", "Opp", "Assicurazione", "Pipeline", "Stage", "Data piano", "€ Tot", "Sigle"]);
    results.patients.forEach((p) => {
      (p.opportunities || []).forEach((op) => {
        rows.push([
          `"${(p.contact_name || "").replace(/"/g, '""')}"`,
          p.contact_id,
          `"${(op.opp_name || "").replace(/"/g, '""')}"`,
          `"${(op.insurance || "").replace(/"/g, '""')}"`,
          `"${(op.pipeline_name || "").replace(/"/g, '""')}"`,
          `"${(op.stage_name || "").replace(/"/g, '""')}"`,
          op.data_piano_cura || "",
          op.total_euro,
          (op.treatment_codes || []).join("|"),
        ]);
      });
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ricerca-pazienti-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results]);

  // Set di codici selezionati (uppercase) per evidenziare le sigle matchate
  // nella tabella quando mode="also" (utente vuole vedere le sigle delle
  // OLTRE prestazioni dello stesso piano di cura).
  const selectedCodesSet = useMemo(
    () => new Set(selectedCodes.map((c) => (c || "").toUpperCase())),
    [selectedCodes],
  );
  const highlightCodes = codesMode === "also" && selectedCodesSet.size > 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-testid="patient-search-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Search className="w-4 h-4 text-indigo-500" />
          Cerca Pazienti per Filtri Composti
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Assicurazione */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Assicurazione</label>
          <select
            value={insurance}
            onChange={(e) => setInsurance(e.target.value)}
            data-testid="search-insurance-select"
            className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white"
          >
            <option value="">Tutte le assicurazioni</option>
            {insurances.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>

        {/* Mesi multi */}
        <MultiSelect
          label="Mesi"
          options={monthOptions.map((m) => ({ value: m.ym, label: m.label }))}
          values={selectedMonths}
          onChange={setSelectedMonths}
          placeholder="Tutti i mesi"
          testid="search-months-multi"
        />

        {/* Stage multi (raggruppato per le 4 pipeline ammesse) */}
        <MultiSelect
          label="Stage"
          groups={stageGroups}
          values={selectedStages}
          onChange={setSelectedStages}
          placeholder="Tutti gli stage"
          testid="search-stages-multi"
        />

        {/* Sigle prestazioni multi (con modalità) */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1">
            Sigle prestazioni
            {insurance && (
              <span className="ml-1 text-slate-400 font-normal">({treatmentCodes.length})</span>
            )}
          </label>
          <MultiSelect
            label={null}
            options={codeOptions}
            values={selectedCodes}
            onChange={setSelectedCodes}
            placeholder={insurance ? (loadingCodes ? "Caricamento…" : "Seleziona sigle…") : "Scegli prima l'assicurazione"}
            testid="search-codes-multi"
            disabled={!insurance || loadingCodes}
          />
        </div>
      </div>

      {/* Modalità sigle (mutualmente esclusive) */}
      {insurance && treatmentCodes.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
          <span className="text-slate-500 font-semibold uppercase tracking-wide">Modalità sigle:</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={codesMode === "also"}
              onChange={() => setCodesMode("also")}
              data-testid="mode-also"
              className="w-4 h-4 text-indigo-500"
            />
            <span className="text-slate-700">Anche con (può avere anche altre)</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={codesMode === "only"}
              onChange={() => setCodesMode("only")}
              data-testid="mode-only"
              className="w-4 h-4 text-indigo-500"
            />
            <span className="text-slate-700">Solo con (esclusivamente queste)</span>
          </label>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={runSearch}
          disabled={searching}
          data-testid="search-submit"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50"
        >
          <Search className="w-4 h-4" />
          {searching ? "Ricerca…" : "Cerca"}
        </button>
        <button
          type="button"
          onClick={resetAll}
          data-testid="search-reset"
          className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm"
        >
          <XIcon className="w-4 h-4" /> Reset
        </button>
        {results?.patients?.length > 0 && (
          <button
            type="button"
            onClick={exportCsv}
            data-testid="search-export-csv"
            className="ml-auto inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm"
          >
            <Download className="w-4 h-4" /> Esporta CSV
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {error}
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          {/* Conteggio totale prominente */}
          <div
            className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3"
            data-testid="search-total-banner"
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-600" />
              <span className="text-[11px] uppercase tracking-wider font-semibold text-indigo-700">
                Tot Pazienti trovati
              </span>
            </div>
            <span
              className="text-2xl font-bold text-indigo-700 tabular-nums"
              data-testid="search-total-patients"
            >
              {results.total_patients}
            </span>
            <span className="text-xs text-slate-500">
              · {results.total_opportunities} {results.total_opportunities === 1 ? "opportunità" : "opportunità"}
            </span>
          </div>

          {results.patients.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400" data-testid="search-empty">
              Nessun paziente trovato con i filtri selezionati.
            </div>
          ) : (
            <>
              {highlightCodes && (
                <div className="mb-2 text-[11px] text-slate-500 flex items-center gap-2">
                  <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[9px] font-mono font-semibold">SIGLA</span>
                  <span>= prestazione richiesta dal filtro</span>
                  <span className="ml-3 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px] font-mono">SIGLA</span>
                  <span>= altre prestazioni nello stesso piano</span>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="search-results-table">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wide">
                      <th className="text-left py-2 px-2 font-semibold">Paziente</th>
                      <th className="text-left py-2 px-2 font-semibold">Opportunità</th>
                      <th className="text-left py-2 px-2 font-semibold">Assicurazione</th>
                      <th className="text-left py-2 px-2 font-semibold">Pipeline / Stage</th>
                      <th className="text-left py-2 px-2 font-semibold">Data piano</th>
                      <th className="text-right py-2 px-2 font-semibold">€</th>
                      <th className="text-left py-2 px-2 font-semibold">Sigle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {results.patients.flatMap((p) =>
                      (p.opportunities || []).map((op, idx) => (
                        <tr key={`${p.contact_id}-${op.opp_id}-${idx}`} data-testid={`search-row-${op.opp_id}`} className="hover:bg-slate-50">
                          <td className="py-2 px-2 font-medium text-slate-800">{idx === 0 ? p.contact_name : ""}</td>
                          <td className="py-2 px-2 text-slate-700 truncate max-w-[200px]" title={op.opp_name}>{op.opp_name || "—"}</td>
                          <td className="py-2 px-2 text-slate-600">{op.insurance || "—"}</td>
                          <td className="py-2 px-2 text-slate-600">
                            <span className="text-[10px] text-slate-400 block">{op.pipeline_name || "—"}</span>
                            <span>{op.stage_name || "—"}</span>
                          </td>
                          <td className="py-2 px-2 text-slate-600 tabular-nums">{op.data_piano_cura || "—"}</td>
                          <td className="py-2 px-2 text-right text-slate-800 font-semibold tabular-nums">
                            {op.total_euro ? `€ ${op.total_euro.toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}
                          </td>
                          <td className="py-2 px-2 text-slate-700">
                            <div className="flex flex-wrap gap-1">
                              {(op.treatment_codes || []).map((c) => {
                                const isMatched = highlightCodes && selectedCodesSet.has((c || "").toUpperCase());
                                return (
                                  <span
                                    key={c}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${
                                      isMatched
                                        ? "bg-indigo-100 text-indigo-700 font-semibold"
                                        : "bg-slate-100 text-slate-600"
                                    }`}
                                  >
                                    {c}
                                  </span>
                                );
                              })}
                              {(op.treatment_codes || []).length === 0 && (
                                <span className="text-slate-300">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
